import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Th, Td, EmptyState, DateInput } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PackageMinus, Download, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ALL = "__all__";
const REASONS = ["EXPIRED", "DAMAGED", "QUALITY_ISSUE", "OTHER_SHRINKAGE"];

/** Abschreibungshistorie: eine Zeile je fachlichem Vorgang. */
export default function WriteOffsTab() {
  const { t, fmtDate, fmtMoney } = useI18n();
  const [data, setData] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [flt, setFlt] = useState({ date_from: "", date_to: "", q: "", reason_code: "", warehouse_id: "", storage_location_id: "", user_email: "" });

  const params = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(flt).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p.toString();
  }, [flt]);

  const load = useCallback(() => {
    api.get(`/inventory/write-offs?${params()}`).then((r) => setData(r.data))
      .catch((e) => { toast.error(apiError(e)); setData({ rows: [] }); });
  }, [params]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    api.get("/storage-locations").then((r) => setLocations(r.data.locations || [])).catch(() => {});
    api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const [reportOpen, setReportOpen] = useState(false);
  const [rep, setRep] = useState(null);
  const [repYear, setRepYear] = useState(new Date().getFullYear());
  const [repQuarter, setRepQuarter] = useState("");

  const loadReport = useCallback((year, quarter) => {
    const p = new URLSearchParams({ year: String(year) });
    if (quarter) p.set("quarter", String(quarter));
    api.get(`/inventory/write-offs/report?${p}`).then((r) => setRep(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(() => { if (reportOpen) loadReport(repYear, repQuarter); }, [reportOpen, repYear, repQuarter, loadReport]);

  const rows = data?.rows || [];
  const locEnabled = !!data?.location_management_enabled;
  const set = (k) => (v) => setFlt({ ...flt, [k]: v === ALL ? "" : v });

  const exportCsv = () => {
    const cols = [
      ["common.date", (r) => `${fmtDate(r.created_at)} ${String(r.created_at || "").slice(11, 16)}`],
      ["inv.productSku", (r) => r.sku],
      ["common.name", (r) => r.product_name],
      ["ex.supplierLot", (r) => r.lot_number || ""],
      ["ex.bbd", (r) => (r.best_before_date ? fmtDate(r.best_before_date) : "")],
      ["ex.supplier", (r) => r.supplier_name || ""],
      ["ex.warehouse", (r) => r.warehouse_name || ""],
      ...(locEnabled ? [["ex.location", (r) => r.location_code || ""]] : []),
      ["ex.quality", (r) => t(`quality.${r.quality}`, r.quality)],
      ["common.quantity", (r) => r.quantity],
      ["wo.reason", (r) => t(`wo.reason.${r.reason_code}`)],
      ["wo.note", (r) => r.reason_text || ""],
      ["wo.unitCost", (r) => fmtMoney(r.unit_cost)],
      ["wo.totalCost", (r) => fmtMoney(r.total_cost)],
      ["tr.user", (r) => r.created_by_email || ""],
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.map(([k]) => esc(t(k))).join(";"),
      ...rows.map((r) => cols.map(([, get]) => esc(get(r))).join(";"))].join("\r\n");
    const url = window.URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${t("wo.historyTitle")}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="write-offs-tab">
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="w-full sm:w-36">
          <label className="text-xs text-muted-foreground">{t("tr.dateFrom")}</label>
          <DateInput className="mt-1" value={flt.date_from} testId="wo-filter-from"
            onChange={set("date_from")} />
        </div>
        <div className="w-full sm:w-36">
          <label className="text-xs text-muted-foreground">{t("tr.dateTo")}</label>
          <DateInput className="mt-1" value={flt.date_to} testId="wo-filter-to"
            onChange={set("date_to")} />
        </div>
        <div className="w-full sm:w-44">
          <label className="text-xs text-muted-foreground">{t("inv.productSku")}</label>
          <Input className="mt-1 h-9" value={flt.q} data-testid="wo-filter-search"
            onChange={(e) => set("q")(e.target.value)} />
        </div>
        <div className="w-full sm:w-44">
          <label className="text-xs text-muted-foreground">{t("wo.reason")}</label>
          <Select value={flt.reason_code || ALL} onValueChange={set("reason_code")}>
            <SelectTrigger className="mt-1 h-9" data-testid="wo-filter-reason"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("ex.all")}</SelectItem>
              {REASONS.map((r) => <SelectItem key={r} value={r}>{t(`wo.reason.${r}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-40">
          <label className="text-xs text-muted-foreground">{t("ex.warehouse")}</label>
          <Select value={flt.warehouse_id || ALL} onValueChange={set("warehouse_id")}>
            <SelectTrigger className="mt-1 h-9" data-testid="wo-filter-warehouse"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("ex.all")}</SelectItem>
              {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {locEnabled && (
          <div className="w-full sm:w-36">
            <label className="text-xs text-muted-foreground">{t("ex.location")}</label>
            <Select value={flt.storage_location_id || ALL} onValueChange={set("storage_location_id")}>
              <SelectTrigger className="mt-1 h-9" data-testid="wo-filter-location"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t("ex.all")}</SelectItem>
                {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="w-full sm:w-48">
          <label className="text-xs text-muted-foreground">{t("tr.user")}</label>
          <Select value={flt.user_email || ALL} onValueChange={set("user_email")}>
            <SelectTrigger className="mt-1 h-9" data-testid="wo-filter-user"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("ex.all")}</SelectItem>
              {users.map((u) => <SelectItem key={u.id || u.email} value={u.email}>{u.email}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" className="h-9" onClick={exportCsv} disabled={!rows.length}
          data-testid="wo-export-csv"><Download className="mr-1.5 h-4 w-4" /> {t("ex.exportCsv")}</Button>
        <Button variant="outline" className="h-9" onClick={() => setReportOpen(true)}
          data-testid="wo-open-report"><FileText className="mr-1.5 h-4 w-4" /> {t("wo.report")}</Button>
        {!!rows.length && (
          <div className="ml-auto text-sm" data-testid="wo-total">
            {t("wo.totalSum")}: <span className="font-mono font-semibold">{fmtMoney(data.total_cost)}</span>
          </div>
        )}
      </div>

      {!data ? <div className="text-muted-foreground">{t("common.loading")}</div> :
        rows.length === 0 ? <EmptyState testId="wo-empty" icon={PackageMinus} title={t("wo.historyTitle")} hint={t("wo.noRows")} /> : (
          <div className="flex-1 min-h-0 overflow-auto rounded-md border border-border bg-card shadow-sm">
            <table className="w-full min-w-[1000px] text-sm">
              <thead className="bg-muted/50"><tr>
                <Th sticky>{t("common.date")}</Th><Th sticky>{t("inv.productSku")}</Th>
                <Th sticky>{t("ex.supplierLot")}</Th><Th sticky>{t("ex.bbd")}</Th>
                <Th sticky>{t("ex.supplier")}</Th><Th sticky>{t("ex.warehouse")}</Th>
                {locEnabled && <Th sticky>{t("ex.location")}</Th>}
                <Th sticky>{t("ex.quality")}</Th><Th sticky right>{t("common.quantity")}</Th>
                <Th sticky>{t("wo.reason")}</Th><Th sticky right>{t("wo.unitCost")}</Th>
                <Th sticky right>{t("wo.totalCost")}</Th><Th sticky>{t("tr.user")}</Th>
              </tr></thead>
              <tbody data-testid="write-offs-table">
                {rows.map((r, i) => (
                  <tr key={r.id} className="hover:bg-muted/40 transition-colors" data-testid={`write-off-row-${i}`}>
                    <Td>{fmtDate(r.created_at)} <span className="text-xs text-muted-foreground">{String(r.created_at || "").slice(11, 16)}</span></Td>
                    <Td><div className="font-medium text-foreground">{r.product_name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{r.sku}</div></Td>
                    <Td mono>{r.lot_number || t("wo.noLot")}</Td>
                    <Td>{r.best_before_date ? fmtDate(r.best_before_date) : "—"}</Td>
                    <Td>{r.supplier_name || "—"}</Td>
                    <Td>{r.warehouse_name || "—"}</Td>
                    {locEnabled && <Td mono>{r.location_code || "—"}</Td>}
                    <Td>{t(`quality.${r.quality}`, r.quality)}</Td>
                    <Td right mono className="font-semibold">{r.quantity}</Td>
                    <Td>{t(`wo.reason.${r.reason_code}`)}
                      {r.reason_text && <div className="text-xs text-muted-foreground">{r.reason_text}</div>}</Td>
                    <Td right mono>{fmtMoney(r.unit_cost)}</Td>
                    <Td right mono className="font-semibold">{fmtMoney(r.total_cost)}</Td>
                    <Td className="text-xs">{r.created_by_email || "—"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <Dialog open={reportOpen} onOpenChange={(o) => !o && setReportOpen(false)}>
        <DialogContent className="max-w-2xl" data-testid="wo-report-dialog">
          <DialogHeader><DialogTitle>{t("wo.reportTitle")}</DialogTitle></DialogHeader>
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-32">
              <label className="text-xs text-muted-foreground">{t("wo.year")}</label>
              <Input type="number" className="mt-1 h-9" value={repYear} data-testid="wo-report-year"
                onChange={(e) => setRepYear(Number(e.target.value) || new Date().getFullYear())} />
            </div>
            <div className="w-44">
              <label className="text-xs text-muted-foreground">{t("wo.period")}</label>
              <Select value={repQuarter || ALL} onValueChange={(v) => setRepQuarter(v === ALL ? "" : v)}>
                <SelectTrigger className="mt-1 h-9" data-testid="wo-report-period"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t("wo.fullYear")}</SelectItem>
                  {[1, 2, 3, 4].map((q) => (
                    <SelectItem key={q} value={String(q)} data-testid={`wo-report-q${q}`}>{t("wo.quarter", { n: q })}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {rep && (
              <div className="ml-auto text-right text-sm" data-testid="wo-report-total">
                <div className="text-xs text-muted-foreground">{t("wo.count")}: {rep.count} · {t("common.quantity")}: {rep.total_quantity}</div>
                <div className="font-mono text-lg font-semibold">{fmtMoney(rep.total_cost, rep.currency)}</div>
              </div>
            )}
          </div>
          {rep && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <div className="mb-1 text-xs font-medium text-muted-foreground">{t("wo.byReason")}</div>
                <table className="w-full text-xs" data-testid="wo-report-by-reason">
                  <tbody>
                    {rep.by_reason.map((r) => (
                      <tr key={r.reason_code} className="border-b border-border">
                        <Td>{t(`wo.reason.${r.reason_code}`)}</Td>
                        <Td right mono>{r.quantity}</Td>
                        <Td right mono className="font-semibold">{fmtMoney(r.total_cost, rep.currency)}</Td>
                      </tr>))}
                    {!rep.by_reason.length && <tr><Td className="text-muted-foreground">{t("wo.noRows")}</Td></tr>}
                  </tbody>
                </table>
              </div>
              <div>
                <div className="mb-1 text-xs font-medium text-muted-foreground">{t("wo.byMonth")}</div>
                <table className="w-full text-xs" data-testid="wo-report-by-month">
                  <tbody>
                    {rep.by_month.map((r) => (
                      <tr key={r.month} className="border-b border-border">
                        <Td mono>{r.month}</Td>
                        <Td right mono>{r.quantity}</Td>
                        <Td right mono className="font-semibold">{fmtMoney(r.total_cost, rep.currency)}</Td>
                      </tr>))}
                    {!rep.by_month.length && <tr><Td className="text-muted-foreground">{t("wo.noRows")}</Td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
